'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  ClaireConfirmedFailure,
  ClaireFailureGroupView,
} from '@borradh-workspace/api-client/types';
import { Inbox, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAssignClaireFailures } from '../../api/claire-failure-groups';
import {
  commonType,
  failureTypeLabel,
  harnessUrl,
  isSeriousType,
  shortDateTime,
} from './failure-format';
import { GroupDialog } from './group-dialog';

/**
 * Replies a person confirmed as Claire's fault that are in no group yet:
 * tick the ones with the same cause, then add them to a group or start one.
 */
export function FailureInbox({
  failures,
  groups,
}: {
  failures: ClaireConfirmedFailure[];
  groups: ClaireFailureGroupView[];
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupId, setGroupId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const { assignFailures, isAssigning } = useAssignClaireFailures({
    onSuccess: () => {
      setSelected(new Set());
      setGroupId('');
      setCreating(false);
    },
  });

  const picked = failures.filter((f) => selected.has(f.observationId));
  const type = commonType(picked.map((f) => f.failureType));
  // Groups about the same failure type first: the likeliest home.
  const choices = useMemo(
    () =>
      [...groups].sort(
        (a, b) =>
          Number(b.failureType === type) - Number(a.failureType === type) ||
          a.title.localeCompare(b.title)
      ),
    [groups, type]
  );

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const allOn = failures.length > 0 && picked.length === failures.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confirmed, not in a group yet ({failures.length})</CardTitle>
        <CardDescription>
          Replies a person marked as Claire’s fault in the triage queue. Tick
          the ones that went wrong for the same reason and add them to a group.
          Customer text is read from Langfuse, with phone numbers and emails
          removed, and is not stored here.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {failures.length === 0 ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Inbox />
              </EmptyMedia>
              <EmptyTitle>Nothing waiting</EmptyTitle>
              <EmptyDescription>
                Every confirmed failure is in a group. New ones appear here once
                someone marks a reply in the triage queue as Claire’s fault.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <div
              className="bg-muted/50 flex flex-wrap items-center gap-2 rounded-md p-2"
              aria-live="polite"
            >
              <span className="text-sm">
                {picked.length
                  ? `${picked.length} selected${type ? ` · ${failureTypeLabel(type)}` : ''}`
                  : 'Select replies to group them'}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Select
                  value={groupId}
                  onValueChange={setGroupId}
                  disabled={!picked.length || !groups.length}
                >
                  <SelectTrigger
                    className="w-64"
                    aria-label="Group to add the selected replies to"
                  >
                    <SelectValue
                      placeholder={
                        groups.length ? 'Choose a group…' : 'No groups yet'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {choices.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  disabled={!picked.length || !groupId || isAssigning}
                  onClick={() =>
                    assignFailures({
                      observationIds: picked.map((f) => f.observationId),
                      groupId,
                    })
                  }
                >
                  Add to group
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!picked.length || isAssigning}
                  onClick={() => setCreating(true)}
                >
                  <Plus />
                  New group
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      aria-label="Select all"
                      checked={allOn}
                      onCheckedChange={(on) =>
                        setSelected(
                          on
                            ? new Set(failures.map((f) => f.observationId))
                            : new Set()
                        )
                      }
                    />
                  </TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Clinic</TableHead>
                  <TableHead>What went wrong</TableHead>
                  <TableHead>Customer → Claire</TableHead>
                  <TableHead>Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((f) => (
                  <TableRow
                    key={f.observationId}
                    data-state={
                      selected.has(f.observationId) ? 'selected' : undefined
                    }
                  >
                    <TableCell>
                      <Checkbox
                        aria-label={`Select the reply sent ${shortDateTime(f.turnAt)}`}
                        checked={selected.has(f.observationId)}
                        onCheckedChange={(on) =>
                          toggle(f.observationId, on === true)
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {shortDateTime(f.turnAt)}
                    </TableCell>
                    <TableCell>{f.clinicName ?? 'Unknown clinic'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          isSeriousType(f.failureType)
                            ? 'destructive'
                            : 'secondary'
                        }
                      >
                        {failureTypeLabel(f.failureType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p
                        className="text-muted-foreground line-clamp-2 text-xs"
                        data-customer-text
                      >
                        “{f.customerMessage || '…'}”
                      </p>
                      <p className="line-clamp-2 text-xs" data-customer-text>
                        → “{f.reply || 'no reply'}”
                      </p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {f.conversationId ? (
                        <a
                          className="underline"
                          href={harnessUrl(f.conversationId)}
                        >
                          Chat
                        </a>
                      ) : null}
                      {f.conversationId && f.langfuseUrl ? ' · ' : null}
                      {f.langfuseUrl ? (
                        <a
                          className="underline"
                          href={f.langfuseUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Trace
                        </a>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
      <GroupDialog
        open={creating}
        onOpenChange={setCreating}
        mode="create"
        replies={picked.length}
        isSaving={isAssigning}
        onSubmit={(values) =>
          assignFailures({
            observationIds: picked.map((f) => f.observationId),
            newGroup: {
              title: values.title,
              description: values.description,
              failureType: type,
            },
          })
        }
      />
    </Card>
  );
}
